import React from 'react';
import { AlertCircle } from 'lucide-react';

interface FieldErrorProps {
  message?: string;
}

/**
 * Inline per-field validation error. Shows only when `message` is truthy.
 * Usage: <FieldError message={errors.email} />
 */
export const FieldError: React.FC<FieldErrorProps> = ({ message }) => {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1.5 mt-1.5 text-[12px] font-medium text-coral-600" role="alert">
      <AlertCircle size={13} className="flex-none" />
      {message}
    </p>
  );
};

/**
 * Lightweight form-error state management hook.
 * Usage:
 *   const { errors, setError, clearError, hasErrors, validate } = useFormErrors<{ email: string; desc: string }>();
 */
export function useFormErrors<T extends Record<string, string>>() {
  const [errors, setErrors] = React.useState<Partial<T>>({});

  const setError = React.useCallback((field: keyof T, message: string) => {
    setErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const clearError = React.useCallback((field: keyof T) => {
    setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  }, []);

  const clearAll = React.useCallback(() => setErrors({}), []);

  const hasErrors = Object.values(errors).some(Boolean);

  /** Run a map of validators, set errors, return true if valid. */
  const validate = React.useCallback(
    (rules: Record<keyof T, () => string | undefined>): boolean => {
      const next: Partial<T> = {};
      let valid = true;
      for (const [field, rule] of Object.entries(rules) as [keyof T, () => string | undefined][]) {
        const msg = rule();
        if (msg) { next[field] = msg as T[keyof T]; valid = false; }
      }
      setErrors(next);
      return valid;
    },
    []
  );

  return { errors, setError, clearError, clearAll, hasErrors, validate };
}
